import {
  ApiError,
  formatDuration,
  formatMoney,
  LONG_OPEN_SHIFT_HOURS,
  queryKeys,
  type RoleKey,
  type StaffPayRates,
  type TimeClockPay,
  type TimeClockSummary,
  type TimeEntryListItem,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { Ban, Banknote, Clock, MoreHorizontal, Pencil, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { DataTable } from '../components/data-table.js'
import { toISODate } from '../components/month-calendar.js'
import { type PaymentPrefill, RecordPaymentDialog } from '../components/record-payment-dialog.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

interface StaffOption {
  id: string
  name: string
  role: RoleKey
  isActive: boolean
}

type Period = 'today' | 'this_week' | 'last_week' | 'this_month' | 'last_month' | 'custom'

const PERIOD_LABELS: Record<Period, string> = {
  today: 'Today',
  this_week: 'This week',
  last_week: 'Last week',
  this_month: 'This month',
  last_month: 'Last month',
  custom: 'Custom dates',
}

/** Calendar days for a preset, as inclusive yyyy-mm-dd. Weeks run Sunday to Saturday. */
function periodRange(period: Exclude<Period, 'custom'>): { from: string; to: string } {
  const today = new Date()
  const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
  switch (period) {
    case 'today':
      return { from: toISODate(startOfDay), to: toISODate(startOfDay) }
    case 'this_week': {
      const start = addDays(startOfDay, -startOfDay.getDay())
      return { from: toISODate(start), to: toISODate(addDays(start, 6)) }
    }
    case 'last_week': {
      const start = addDays(startOfDay, -startOfDay.getDay() - 7)
      return { from: toISODate(start), to: toISODate(addDays(start, 6)) }
    }
    case 'this_month':
      return {
        from: toISODate(new Date(today.getFullYear(), today.getMonth(), 1)),
        to: toISODate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      }
    case 'last_month':
      return {
        from: toISODate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        to: toISODate(new Date(today.getFullYear(), today.getMonth(), 0)),
      }
  }
}

/** `<input type="datetime-local">` value for an ISO instant, in the browser's zone. */
function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function fromLocalInput(value: string): string {
  return new Date(value).toISOString()
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
}

const STATUS_OPTIONS = [
  ['active', 'Open and closed'],
  ['open', 'Open (on the clock)'],
  ['closed', 'Closed'],
  ['voided', 'Voided'],
  ['all', 'Everything'],
] as const

/**
 * Staff time clock (t1106), manager view: hours per person for a period, every
 * punch behind them, and the tools to fix a missed or wrong one. Staff punch
 * themselves from the header button; this page is for correcting and totalling.
 * Same shape as the rentals list — infinite scroll, not page numbers.
 */
export function TimeClockPage() {
  const { can } = useAuth()
  const [staffId, setStaffId] = useState('all')
  const [period, setPeriod] = useState<Period>('this_week')
  const [customFrom, setCustomFrom] = useState(() => periodRange('this_week').from)
  const [customTo, setCustomTo] = useState(() => periodRange('this_week').to)
  const [status, setStatus] = useState<string>('active')
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<TimeEntryListItem | null>(null)
  const [voiding, setVoiding] = useState<TimeEntryListItem | null>(null)
  const [ratesOpen, setRatesOpen] = useState(false)
  const [paying, setPaying] = useState<PaymentPrefill | null>(null)

  // Pay is sensitive: rates and pay follow the payments permissions, not the
  // time clock's, so a manager of hours alone sees hours and nothing else.
  const canSeePay = can('expenses:read')
  const canPay = can('expenses:manage')

  const canListStaff = can('staff:read')
  const { data: staffList } = useQuery({
    queryKey: queryKeys.staff.list(),
    queryFn: () => api.get<StaffOption[]>('/staff'),
    enabled: canListStaff,
  })
  const staffOptions = useMemo(
    () => (staffList ?? []).filter((s) => s.isActive && s.role !== 'guest'),
    [staffList],
  )

  const range = period === 'custom' ? { from: customFrom, to: customTo } : periodRange(period)
  const rangeValid = /^\d{4}-\d{2}-\d{2}$/.test(range.from) && /^\d{4}-\d{2}-\d{2}$/.test(range.to)
  const userId = staffId === 'all' ? undefined : staffId

  const summaryFilters = { from: range.from, to: range.to, userId }
  const summary = useQuery({
    queryKey: queryKeys.timeClock.summary(summaryFilters),
    queryFn: () => api.get<TimeClockSummary>('/time-clock/summary', { query: summaryFilters }),
    enabled: rangeValid,
  })

  const pay = useQuery({
    queryKey: queryKeys.timeClock.pay(summaryFilters),
    queryFn: () => api.get<TimeClockPay>('/time-clock/pay', { query: summaryFilters }),
    enabled: rangeValid && canSeePay,
  })
  const payByUser = useMemo(
    () => new Map((pay.data?.rows ?? []).map((r) => [r.userId, r])),
    [pay.data],
  )

  const filters = { ...summaryFilters, status, pageSize: 50 }
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.timeClock.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: TimeEntryListItem[]; total: number; page: number; pageSize: number }>(
        '/time-clock/entries',
        { query: { ...filters, page: pageParam } },
      ),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
    enabled: rangeValid,
  })
  const rows = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  const columns = useMemo<ColumnDef<TimeEntryListItem>[]>(
    () => [
      {
        id: 'staff',
        header: 'Staff',
        cell: ({ row }) => <span className="font-medium">{row.original.userName}</span>,
      },
      {
        id: 'in',
        header: 'Clock in',
        cell: ({ row }) => formatWhen(row.original.clockInAt),
      },
      {
        id: 'out',
        header: 'Clock out',
        cell: ({ row }) => {
          const e = row.original
          if (e.status === 'voided' && !e.clockOutAt)
            return <span className="text-foreground-muted">—</span>
          if (e.clockOutAt) return formatWhen(e.clockOutAt)
          const openMs = Date.now() - new Date(e.clockInAt).getTime()
          const stale = openMs > LONG_OPEN_SHIFT_HOURS * 3_600_000
          return stale ? (
            <Badge tone="warning" title="Probably a forgotten clock-out — correct it">
              Open · {formatDuration(openMs)}
            </Badge>
          ) : (
            <Badge tone="success">On the clock</Badge>
          )
        },
      },
      {
        id: 'duration',
        header: 'Duration',
        cell: ({ row }) =>
          row.original.durationMs != null ? (
            <span className="tabular-nums">{formatDuration(row.original.durationMs)}</span>
          ) : (
            <span className="text-foreground-muted">—</span>
          ),
      },
      {
        id: 'flags',
        header: '',
        cell: ({ row }) => {
          const e = row.original
          return (
            <div className="flex flex-wrap gap-1">
              {e.status === 'voided' ? (
                <Badge tone="danger" title={`${e.voidReason ?? ''} — ${e.voidedByName ?? ''}`}>
                  Voided
                </Badge>
              ) : null}
              {e.source === 'admin' ? <Badge tone="info">Added by manager</Badge> : null}
              {e.editedAt && e.status !== 'voided' ? (
                <Badge tone="neutral" title={`${formatWhen(e.editedAt)} — ${e.editedByName ?? ''}`}>
                  Edited
                </Badge>
              ) : null}
            </div>
          )
        },
      },
      {
        id: 'note',
        header: 'Note',
        cell: ({ row }) => (
          <span
            className="block max-w-64 truncate text-foreground-muted"
            title={row.original.note ?? ''}
          >
            {row.original.note ?? ''}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) =>
          row.original.status === 'voided' ? null : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Entry actions">
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setEditing(row.original)}>
                  <Pencil /> Correct times
                </DropdownMenuItem>
                <DropdownMenuItem destructive onSelect={() => setVoiding(row.original)}>
                  <Ban /> Void entry
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ),
      },
    ],
    [],
  )

  const filtered = staffId !== 'all' || status !== 'active'

  return (
    <Page>
      <PageHeader
        title="Time clock"
        description="Hours worked, per person. Staff clock in and out from the button at the top right."
        actions={
          <>
            {canSeePay ? (
              <Button variant="secondary" onClick={() => setRatesOpen(true)}>
                <Banknote /> Pay rates
              </Button>
            ) : null}
            {canListStaff ? (
              <Button onClick={() => setAdding(true)}>
                <Plus /> Add entry
              </Button>
            ) : null}
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {canListStaff ? (
          <Select value={staffId} onValueChange={setStaffId}>
            <SelectTrigger className="w-48" aria-label="Staff member">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All staff</SelectItem>
              {staffOptions.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}

        <Select value={period} onValueChange={(v) => setPeriod(v as Period)}>
          <SelectTrigger className="w-44" aria-label="Period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
              <SelectItem key={p} value={p}>
                {PERIOD_LABELS[p]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {period === 'custom' ? (
          <>
            <Input
              type="date"
              className="w-40"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              aria-label="From date"
            />
            <span className="text-sm text-foreground-muted">to</span>
            <Input
              type="date"
              className="w-40"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              aria-label="To date"
            />
          </>
        ) : (
          <span className="text-sm text-foreground-muted">
            {range.from === range.to ? range.from : `${range.from} – ${range.to}`}
          </span>
        )}

        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48" aria-label="Entry status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Hours for this period</CardTitle>
        </CardHeader>
        <CardContent>
          {!rangeValid ? (
            <p className="text-sm text-foreground-muted">Pick a start and end date.</p>
          ) : summary.isError ? (
            <Alert tone="danger">Could not load the totals.</Alert>
          ) : summary.isLoading ? (
            <p className="text-sm text-foreground-muted">Loading…</p>
          ) : summary.data && summary.data.rows.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-start text-foreground-muted">
                  <th className="py-2 pe-4 text-start font-medium">Staff</th>
                  <th className="py-2 pe-4 text-end font-medium">Shifts</th>
                  <th className="py-2 pe-4 text-end font-medium">Total hours</th>
                  {canSeePay ? (
                    <>
                      <th className="py-2 pe-4 text-end font-medium">Rate</th>
                      <th className="py-2 pe-4 text-end font-medium">Pay</th>
                    </>
                  ) : null}
                  <th className="py-2 text-start font-medium" />
                </tr>
              </thead>
              <tbody>
                {summary.data.rows.map((r) => {
                  const p = payByUser.get(r.userId)
                  return (
                    <tr key={r.userId} className="border-b border-border last:border-0">
                      <td className="py-2 pe-4">
                        <button
                          type="button"
                          className="font-medium hover:underline"
                          onClick={() => setStaffId(r.userId)}
                        >
                          {r.userName}
                        </button>
                      </td>
                      <td className="py-2 pe-4 text-end tabular-nums">{r.entryCount}</td>
                      <td className="py-2 pe-4 text-end font-medium tabular-nums">
                        {formatDuration(r.totalMs)}
                      </td>
                      {canSeePay ? (
                        <>
                          <td className="py-2 pe-4 text-end tabular-nums">
                            {p && p.rates.length > 0
                              ? p.rates.map((c) => `${formatMoney(c)}/h`).join(' · ')
                              : '—'}
                          </td>
                          <td className="py-2 pe-4 text-end font-medium tabular-nums">
                            {p ? formatMoney(p.payCents) : pay.isLoading ? '…' : '—'}
                          </td>
                        </>
                      ) : null}
                      <td className="py-2">
                        <div className="flex flex-wrap items-center gap-1">
                          {r.hasOpenEntry ? (
                            <Badge
                              tone="success"
                              title="Still clocked in — not counted until they clock out"
                            >
                              On the clock
                            </Badge>
                          ) : null}
                          {p && p.unratedMs > 0 ? (
                            <Badge
                              tone="warning"
                              title={`${formatDuration(p.unratedMs)} worked before a pay rate was set — not priced`}
                            >
                              No rate
                            </Badge>
                          ) : null}
                          {canPay && p && p.payCents > 0 ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                setPaying({
                                  kind: 'staff',
                                  staffUserId: r.userId,
                                  amountCents: p.payCents,
                                  note: `Hours ${range.from} – ${range.to}: ${formatDuration(p.ratedMs)} at ${p.rates.map((c) => `${formatMoney(c)}/h`).join(' / ')}`,
                                })
                              }
                            >
                              Record payment
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td className="pt-3 pe-4 font-semibold">Total</td>
                  <td />
                  <td className="pt-3 pe-4 text-end font-semibold tabular-nums">
                    {formatDuration(summary.data.totalMs)}
                  </td>
                  {canSeePay ? (
                    <>
                      <td />
                      <td className="pt-3 pe-4 text-end font-semibold tabular-nums">
                        {pay.data ? formatMoney(pay.data.totalPayCents) : ''}
                      </td>
                    </>
                  ) : null}
                  <td />
                </tr>
              </tfoot>
            </table>
          ) : (
            <p className="text-sm text-foreground-muted">No hours recorded in this period.</p>
          )}
        </CardContent>
      </Card>

      <DataTable
        data={rows}
        columns={columns}
        isLoading={isLoading}
        getRowId={(row) => row.id}
        infinite={{
          total,
          hasMore: Boolean(hasNextPage),
          isFetchingMore: isFetchingNextPage,
          onLoadMore: () => void fetchNextPage(),
        }}
        empty={
          filtered ? (
            <EmptyState
              icon={Clock}
              variant="no-results"
              title="No entries match"
              description="Try another person, period or status."
              onClearFilters={() => {
                setStaffId('all')
                setStatus('active')
              }}
            />
          ) : (
            <EmptyState
              icon={Clock}
              title="No time entries in this period"
              description="Entries appear here as staff clock in and out."
            />
          )
        }
      />

      {adding ? (
        <EntryDialog staff={staffOptions} entry={null} onClose={() => setAdding(false)} />
      ) : null}
      {editing ? (
        <EntryDialog staff={staffOptions} entry={editing} onClose={() => setEditing(null)} />
      ) : null}
      {voiding ? <VoidDialog entry={voiding} onClose={() => setVoiding(null)} /> : null}
      {ratesOpen ? <PayRatesDialog canEdit={canPay} onClose={() => setRatesOpen(false)} /> : null}
      {canPay ? (
        <RecordPaymentDialog
          open={paying !== null}
          onClose={() => setPaying(null)}
          prefill={paying ?? undefined}
        />
      ) : null}
    </Page>
  )
}

/** Adds a missed entry (no `entry`) or corrects an existing one. */
function EntryDialog({
  staff,
  entry,
  onClose,
}: {
  staff: StaffOption[]
  entry: TimeEntryListItem | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [userId, setUserId] = useState(entry?.userId ?? '')
  const [clockIn, setClockIn] = useState(toLocalInput(entry?.clockInAt ?? null))
  const [clockOut, setClockOut] = useState(toLocalInput(entry?.clockOutAt ?? null))
  const [note, setNote] = useState(entry?.note ?? '')
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () => {
      if (!entry) {
        return api.post('/time-clock/entries', {
          userId,
          clockInAt: fromLocalInput(clockIn),
          ...(clockOut ? { clockOutAt: fromLocalInput(clockOut) } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        })
      }
      // Send only what changed, so an untouched field is never rewritten.
      const patch: Record<string, unknown> = {}
      if (clockIn !== toLocalInput(entry.clockInAt)) patch.clockInAt = fromLocalInput(clockIn)
      if (clockOut && clockOut !== toLocalInput(entry.clockOutAt)) {
        patch.clockOutAt = fromLocalInput(clockOut)
      }
      if (note.trim() !== (entry.note ?? '')) patch.note = note.trim() || null
      return api.patch(`/time-clock/entries/${entry.id}`, patch)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.timeClock.all() })
      toast.success(entry ? 'Entry corrected' : 'Entry added')
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Something went wrong'),
  })

  const valid = Boolean(clockIn) && (entry !== null || Boolean(userId))

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {entry ? `Correct ${entry.userName}’s entry` : 'Add a time entry'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {!entry ? (
            <Field label="Staff member" required>
              {(props) => (
                <Select value={userId} onValueChange={setUserId}>
                  <SelectTrigger {...props}>
                    <SelectValue placeholder="Choose someone" />
                  </SelectTrigger>
                  <SelectContent>
                    {staff.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          ) : null}
          <Field label="Clock in" required>
            {(props) => (
              <Input
                {...props}
                type="datetime-local"
                value={clockIn}
                onChange={(e) => setClockIn(e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Clock out"
            hint={
              entry && !entry.clockOutAt
                ? 'Leave blank to keep this shift open.'
                : entry
                  ? undefined
                  : 'Leave blank if they are still on the clock.'
            }
          >
            {(props) => (
              <Input
                {...props}
                type="datetime-local"
                value={clockOut}
                onChange={(e) => setClockOut(e.target.value)}
              />
            )}
          </Field>
          <Field label="Note">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
          {error ? <Alert tone="danger">{error}</Alert> : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={save.isPending}
            onClick={() => {
              setError(null)
              save.mutate()
            }}
          >
            {entry ? 'Save changes' : 'Add entry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function VoidDialog({ entry, onClose }: { entry: TimeEntryListItem; onClose: () => void }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const confirm = useMutation({
    mutationFn: () => api.post(`/time-clock/entries/${entry.id}/void`, { reason: reason.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.timeClock.all() })
      toast.success('Entry voided')
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Something went wrong'),
  })

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Void this entry?</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-foreground-muted">
            {entry.userName}, {formatWhen(entry.clockInAt)}
            {entry.clockOutAt ? ` to ${formatWhen(entry.clockOutAt)}` : ' (still open)'}. A voided
            entry stays on record but stops counting toward hours.
          </p>
          <Field label="Reason" required>
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          {error ? <Alert tone="danger">{error}</Alert> : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={reason.trim().length < 3}
            loading={confirm.isPending}
            onClick={() => {
              setError(null)
              confirm.mutate()
            }}
          >
            Void entry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Hourly rates per person (t1188). A new rate applies from its date; earlier hours keep the old one. */
function PayRatesDialog({ canEdit, onClose }: { canEdit: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [userId, setUserId] = useState('')
  const [rate, setRate] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState(() => toISODate(new Date()))
  const [error, setError] = useState<string | null>(null)

  const rates = useQuery({
    queryKey: queryKeys.timeClock.payRates(),
    queryFn: () => api.get<StaffPayRates>('/time-clock/pay-rates'),
  })
  const people = rates.data?.rows ?? []
  const selected = people.find((p) => p.userId === userId)

  const rateCents = Math.round(Number(rate || 0) * 100)
  const valid =
    Boolean(userId) &&
    rateCents > 0 &&
    rateCents <= 100_000_00 &&
    /^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom)

  const save = useMutation({
    mutationFn: () => api.post('/time-clock/pay-rates', { userId, rateCents, effectiveFrom }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.timeClock.all() })
      toast.success('Pay rate saved')
      setRate('')
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Something went wrong'),
  })

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Hourly pay rates</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {rates.isError ? (
            <Alert tone="danger">Could not load the pay rates.</Alert>
          ) : rates.isLoading ? (
            <p className="text-sm text-foreground-muted">Loading…</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-foreground-muted">
                  <th className="py-2 pe-4 text-start font-medium">Staff</th>
                  <th className="py-2 text-end font-medium">Current rate</th>
                </tr>
              </thead>
              <tbody>
                {people.map((p) => (
                  <tr key={p.userId} className="border-b border-border last:border-0">
                    <td className="py-2 pe-4">
                      {canEdit ? (
                        <button
                          type="button"
                          className="font-medium hover:underline"
                          onClick={() => setUserId(p.userId)}
                        >
                          {p.userName}
                        </button>
                      ) : (
                        <span className="font-medium">{p.userName}</span>
                      )}
                    </td>
                    <td className="py-2 text-end tabular-nums">
                      {p.currentRateCents != null ? (
                        `${formatMoney(p.currentRateCents)}/h`
                      ) : (
                        <span className="text-foreground-muted">Not set</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {canEdit ? (
            <div className="space-y-4 border-t border-border pt-4">
              <Field label="Staff member" required>
                {(props) => (
                  <Select value={userId} onValueChange={setUserId}>
                    <SelectTrigger {...props}>
                      <SelectValue placeholder="Choose someone" />
                    </SelectTrigger>
                    <SelectContent>
                      {people.map((p) => (
                        <SelectItem key={p.userId} value={p.userId}>
                          {p.userName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Hourly rate ($)" required>
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={rate}
                      onChange={(e) => setRate(e.target.value)}
                    />
                  )}
                </Field>
                <Field
                  label="Applies from"
                  required
                  hint="Hours worked before this day keep their old rate."
                >
                  {(props) => (
                    <Input
                      {...props}
                      type="date"
                      value={effectiveFrom}
                      onChange={(e) => setEffectiveFrom(e.target.value)}
                    />
                  )}
                </Field>
              </div>
              {selected && selected.history.length > 0 ? (
                <div className="text-sm">
                  <p className="mb-1 font-medium">Rate history for {selected.userName}</p>
                  <ul className="space-y-0.5 text-foreground-muted">
                    {selected.history.map((h) => (
                      <li key={h.id} className="tabular-nums">
                        {formatMoney(h.rateCents)}/h from {h.effectiveFrom}
                        {h.createdByName ? ` — set by ${h.createdByName}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {error ? <Alert tone="danger">{error}</Alert> : null}
            </div>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {canEdit ? 'Close' : 'Done'}
          </Button>
          {canEdit ? (
            <Button
              variant="primary"
              disabled={!valid}
              loading={save.isPending}
              onClick={() => {
                setError(null)
                save.mutate()
              }}
            >
              Save rate
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
